import { SignIn, GenSnackBar } from '@pinnacleautomotive/clarity-components';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import logo from '../../assets/diamonddigital-logo.svg';
import { UserPersonStatus } from '../../common/enums/enums';
import { amplifyApi } from '../../hooks/amplifyApi';
import {
  clearSaveUserProfileStatus,
  clearVerificationCodeResent,
  openProfileManagerDialog,
  resendVerificationCode,
  saveUserProfile,
  sendVerificationCode,
  verifyCode,
} from '../../redux/actions/userManagementActions';
import {
  selectIsSavingProfile,
  selectPersonId,
  selectSaveProfileStatus,
  selectUserPerson,
  selectVerificationCodeResent,
  selectVerificationLoading,
  selectVerificationModalOpen,
  selectVerificationStatus,
} from '../../redux/selectors/userDataSelectors';

const AppSignInManager = () => {
  const { t } = useTranslation('global');
  const dispatch = useDispatch();
  const person = useSelector(selectUserPerson);
  const isLoading = useSelector(selectIsSavingProfile);
  const personId = useSelector(selectPersonId);
  const saveProfileStatus = useSelector(selectSaveProfileStatus);
  const verificationCodeResent = useSelector(selectVerificationCodeResent);
  const verificationStatus = useSelector(selectVerificationStatus);
  const verificationLoading = useSelector(selectVerificationLoading);
  const verificationModalOpen = useSelector(selectVerificationModalOpen);

  const saveProfile = newData => {
    const payload = { ...newData };
    payload.status = UserPersonStatus.ACTIVE;
    payload.photo === person.photo && delete payload.photo;
    dispatch(saveUserProfile({ personId, data: payload, isLogIn: true }));
  };

  const clearSaveStatus = () => {
    dispatch(clearSaveUserProfileStatus());
  };

  useEffect(() => {
    if (saveProfileStatus !== 'success' || !verificationModalOpen) return;
    dispatch(sendVerificationCode({ personId }));
  }, [dispatch, personId, saveProfileStatus, verificationModalOpen]);

  const resendVerificationCodeHandler = () => {
    dispatch(resendVerificationCode({ personId }));
  };

  const verifyCodeHandler = code => {
    dispatch(verifyCode({ personId, code }));
  };

  const closeResendVerificationCodeSnackBar = () => {
    dispatch(clearVerificationCodeResent());
  };

  const errorSnackBarOpen =
    saveProfileStatus === 'error' || verificationStatus === 'error';

  return (
    <>
      <GenSnackBar
        open={verificationStatus === 'success'}
        title={t('profileManager.verificationSuccess')}
        onClose={clearSaveStatus}
      />
      <GenSnackBar
        open={errorSnackBarOpen}
        title={t('profileManager.error')}
        type="error"
        onClose={clearSaveStatus}
      />
      <GenSnackBar
        message={'A new verification code has been sent by SMS'}
        open={verificationCodeResent}
        icon={false}
        color={'#323232'}
        onClose={closeResendVerificationCodeSnackBar}
      />
      <SignIn
        isVendor={false}
        logo={logo}
        amplifyApi={amplifyApi}
        data={person}
        onSave={saveProfile}
        isLoading={isLoading}
        profileStatus={saveProfileStatus}
        successScreenOnClick={clearSaveStatus}
        verificationCodeOpen={verificationModalOpen}
        onGoBack={() => dispatch(openProfileManagerDialog())}
        onResend={resendVerificationCodeHandler}
        onVerify={verifyCodeHandler}
        verificationCodeLoading={verificationLoading}
        verificationStatus={verificationStatus}
        verificationCodeResent={verificationCodeResent}
        error={
          verificationStatus === 'error' && t('profileManager.invalidCode')
        }
      />
    </>
  );
};

export default AppSignInManager;
